import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware } from 'redux';
import reduxThunk from 'redux-thunk';
import { logger } from 'redux-logger';
import { ToastContainer } from 'react-toastify';

import reducers from './reducers';

import ErrorBoundary from './pages/common/ErrorBoundary';
import Routes from './Routes';
import axios from 'axios';
import NetworkService from './network-service';
import ReactGA from 'react-ga';
import { decryptLoginData, getDialCodeFromCountryCode } from './modules/utils';

//for cache
import CacheBuster from './CacheBuster';
import TagManager from 'react-gtm-module';
import Cookies from 'universal-cookie';
import 'react-loading-skeleton/dist/skeleton.css'

const createStoreWithMiddleware =
	process.env.NODE_ENV == 'development'
		? applyMiddleware(reduxThunk)(createStore)
		: applyMiddleware(reduxThunk)(createStore);

const store = createStoreWithMiddleware(reducers, window.devToolsExtension ? window?.devToolsExtension() : f => f);

NetworkService.setupInterceptors(store, window.devToolsExtension ? window.devToolsExtension() : f => f);

const App = () => {
	let cookies = new Cookies();

	window.mobileAndTabletCheck = function () {
		let check = false;
		(function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
		return check;
	};

	useEffect(async () => {
		const tagManagerArgs = {
			gtmId: 'GTM-MLX77DM'
		}
		await process.env.REACT_APP_ENVIRONMENT == "LIVE" && TagManager.initialize(tagManagerArgs);
		await process.env.REACT_APP_ENVIRONMENT == "LIVE" && ReactGA.initialize('UA-191894501-1');
		process.env.REACT_APP_ENVIRONMENT == "LIVE" && localStorage.getItem("fieldCampLogin") && setTimeout(() => {
			let name = decryptLoginData()?.first_name + " " + decryptLoginData()?.last_name
			let phone_number = (decryptLoginData().phone_country_code ? getDialCodeFromCountryCode(decryptLoginData().phone_country_code) : "+1") + "-" + decryptLoginData().phone_number
			let alternate_phone_number = (decryptLoginData().alternate_phone_country_code ? getDialCodeFromCountryCode(decryptLoginData().alternate_phone_country_code) : "+1") + "-" + decryptLoginData().alternate_phone_number
			window.hj('identify', decryptLoginData().id, {
				'user_id': decryptLoginData()?.id,
				'email': decryptLoginData()?.email,
				'name': localStorage.getItem("fieldCampLogin") && decryptLoginData()?.name ? decryptLoginData()?.name : name,
				'user_role': decryptLoginData()?.user_type,
				'mixpanel_link': `https://mixpanel.com/project/2262869/view/2809985/app/profile#distinct_id=${decryptLoginData().id}`,
				'registration_date': decryptLoginData()?.registration_date,
				'subscription_plan': decryptLoginData()?.subscription_plan,
				'subscription_status': decryptLoginData()?.subscription_status,
				'organization_name': decryptLoginData().organization_name ? decryptLoginData().organization_name : "N/A",
				'phone_country_code': decryptLoginData().phone_number ? (decryptLoginData().phone_country_code ? getDialCodeFromCountryCode(decryptLoginData().phone_country_code) : "+1") : "N/A",
				'phone_number': decryptLoginData().phone_number ? phone_number : "N/A",
				'alternate_phone_country_code': decryptLoginData().alternate_phone_number ? (decryptLoginData().alternate_phone_country_code ? getDialCodeFromCountryCode(decryptLoginData().alternate_phone_country_code) : "+1") : "N/A",
				'alternate_phone_number': decryptLoginData().alternate_phone_number ? alternate_phone_number : "N/A",
			});
			new Cookies().set('userId', decryptLoginData().id);

		}, 1000);

		window.productFruitsUser = {
			username: localStorage.getItem("fieldCampLogin") && decryptLoginData()?.email, // REQUIRED, must be unique 
			email: localStorage.getItem("fieldCampLogin") && decryptLoginData()?.email,
			firstname: localStorage.getItem("fieldCampLogin") && decryptLoginData()?.first_name,
			lastname: localStorage.getItem("fieldCampLogin") && decryptLoginData()?.last_name,
			signUpAt: localStorage.getItem("fieldCampLogin") && decryptLoginData()?.registration_date, // Sign up date (yyyy-mm-ddThh:mm:ss)
		};
		(function (w, d, u, c, l) {
			w.productFruits = w.productFruits || {};
			w.productFruits.language = l; w.productFruits.code = c;
			var a = d.getElementsByTagName('head')[0];
			var r = d.createElement('script'); r.async = 1;
			r.src = u + '?c=' + c;
			a.appendChild(r);
		})(window, document, 'https://app.productfruits.com/static/script.js', 'r1aNcpW-DFsmwBPG', 'en'); // Replace 'en' with a proper language code
	}, [])

	if (process.env.REACT_APP_ENVIRONMENT == "LIVE") {
		console.log = () => { };
		console.warn = () => { };
		console.error = () => { }
	}

	return (
		<CacheBuster>
			{({ loading, isLatestVersion, refreshCacheAndReload }) => {
				if (loading) return null;
				console.log(isLatestVersion);
				if (!loading && !isLatestVersion) {
					refreshCacheAndReload();
				}
				return (
					<Provider store={store}>
						<ErrorBoundary>
							<Routes />
						</ErrorBoundary>
						<ToastContainer
							position="top-right"
							autoClose={3000}
							hideProgressBar
							newestOnTop
							closeOnClick
							rtl={false}
							pauseOnFocusLoss
							draggable
							pauseOnHover
						/>

					</Provider>
				)
			}}
		</CacheBuster>
	);
};

export default App;
